import { isWebview } from './deviceDetection';

type PostMessageHandler = {
  postMessage: (data: string) => void;
};

type CordovaWebViewWindow = Window & {
  webkit?: {
    messageHandlers?: {
      cordova_iab?: PostMessageHandler;
    };
  };
  cordova_iab?: PostMessageHandler;
};

type PostMessagePayload = Record<string, unknown>;

const isObjectPayload = (value: unknown): value is PostMessagePayload => {
  return typeof value === 'object' && value !== null;
};

const getCordovaHandlers = (): {
  ios: PostMessageHandler | null;
  android: PostMessageHandler | null;
} => {
  if (typeof window === 'undefined') {
    return { ios: null, android: null };
  }

  const cordovaWindow = window as CordovaWebViewWindow;

  return {
    ios: cordovaWindow.webkit?.messageHandlers?.cordova_iab ?? null,
    android: cordovaWindow.cordova_iab ?? null,
  };
};

/**
 * 傳送訊息給 app 端（iOS 或 Android）。
 * 僅於 WebView 環境使用，且僅接受物件型別資料。
 */
export const postMessage = (data: PostMessagePayload): boolean => {
  if (!isObjectPayload(data)) {
    console.warn('postMessage: data 必須為物件且不可為 null');
    return false;
  }

  if (!isWebview()) {
    console.warn('postMessage 僅支援 WebView 環境');
    return false;
  }

  const jsonData = JSON.stringify(data);
  const { ios, android } = getCordovaHandlers();

  try {
    if (ios) {
      ios.postMessage(jsonData);
      return true;
    }

    if (android) {
      android.postMessage(jsonData);
      return true;
    }

    console.warn('postMessage: 未找到可用的 postMessage handler');
    return false;
  } catch (error) {
    console.error('postMessage 發送失敗', error);
    return false;
  }
};

export const checkPostMessageAvailable = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (!isWebview()) return false;

  const { ios, android } = getCordovaHandlers();
  return Boolean(ios || android);
};

export const isIOSBridgeAvailable = (): boolean => {
  const { ios } = getCordovaHandlers();
  return Boolean(ios);
};

export default {
  postMessage,
  checkPostMessageAvailable,
  isIOSBridgeAvailable,
};
